if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      if (this.querySelector('form')) {
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
      else {
        this.querySelector('[name=id]').disabled = false;
        this.querySelector('button[type=submit]').addEventListener('click', this.onSubmitHandler.bind(this));
      }
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.initBundleTotalPrice();
    }

    getFormData() {
      const formData = this.form ? new FormData(this.form) : new FormData();

      if (!this.form) {
        const idInput = this.querySelector('[name=id]');
        const quantityInput = this.querySelector('[name=quantity]');

        if (idInput) formData.append('id', idInput.value);
        if (quantityInput) formData.append('quantity', quantityInput.value);
      }

      return formData;
    }

    getQuantity(formData) {
      const quantity = Number(formData.get('quantity') || 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getLineItemProperties(formData) {
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match || value === '') continue;
        properties[match[1]] = value;
      }

      return Object.keys(properties).length ? properties : null;
    }

    getBundleData() {
      return this.querySelector('[data-bundle-sell-data]');
    }

    getBundleVariantIds() {
      const bundleData = this.getBundleData();
      if (!bundleData) return [];

      return Array.from(
        bundleData.querySelectorAll('[data-bundle-variant-id][data-bundle-available="true"]')
      )
        .map((item) => Number(item.dataset.bundleVariantId))
        .filter((variantId) => Number.isFinite(variantId) && variantId > 0);
    }

    getBundleItems(formData) {
      const bundleVariantIds = this.getBundleVariantIds();

      if (!bundleVariantIds.length) return null;

      const quantity = this.getQuantity(formData);
      const mainVariantId = Number(formData.get('id'));
      if (!Number.isFinite(mainVariantId) || mainVariantId <= 0) return null;

      const itemsByVariantId = new Map();
      const addItem = (item) => {
        const existingItem = itemsByVariantId.get(item.id);
        if (existingItem) {
          existingItem.quantity += item.quantity;
          return;
        }
        itemsByVariantId.set(item.id, item);
      };

      const mainItem = {
        id: mainVariantId,
        quantity,
      };
      const properties = this.getLineItemProperties(formData);
      const sellingPlan = formData.get('selling_plan');

      if (properties) mainItem.properties = properties;
      if (sellingPlan) mainItem.selling_plan = sellingPlan;

      addItem(mainItem);
      bundleVariantIds.forEach((variantId) => addItem({ id: variantId, quantity }));

      return Array.from(itemsByVariantId.values());
    }

    initBundleTotalPrice() {
      this.bundleTotalPrice = this.querySelector('[data-bundle-total-price]');
      if (!this.bundleTotalPrice) return;

      this.updateBundleTotalPrice();

      const variantIdInput = this.querySelector('[name=id]');
      if (variantIdInput) {
        variantIdInput.addEventListener('change', this.updateBundleTotalPrice.bind(this));
      }

      const quantityInput = this.querySelector('[name=quantity]');
      if (quantityInput) {
        quantityInput.addEventListener('input', this.updateBundleTotalPrice.bind(this));
        quantityInput.addEventListener('change', this.updateBundleTotalPrice.bind(this));
      }
    }

    getCurrentMainVariantPrice() {
      const variantId = this.querySelector('[name=id]')?.value;
      if (!variantId) return null;

      const priceElement = Array.from(
        this.querySelectorAll('[data-bundle-main-variant-price]')
      ).find((item) => item.dataset.variantId === variantId);

      if (!priceElement) return null;

      const price = Number(priceElement.dataset.price);
      return Number.isFinite(price) ? price : null;
    }

    updateBundleTotalPrice() {
      const totalPriceElement = this.querySelector('[data-bundle-total-price]');
      const bundleData = this.getBundleData();
      if (!totalPriceElement || !bundleData) return;

      const mainVariantPrice = this.getCurrentMainVariantPrice();
      const fixedBundlePrice = Number(bundleData.dataset.bundleFixedPrice || 0);
      if (!Number.isFinite(mainVariantPrice) || !Number.isFinite(fixedBundlePrice)) return;

      const total = (mainVariantPrice + fixedBundlePrice) * this.getQuantity(this.getFormData());

      totalPriceElement.textContent = typeof formatMoney === 'function'
        ? formatMoney(total)
        : totalPriceElement.dataset.fallbackText;
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      const spinner = this.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const formData = this.getFormData();
      const bundleItems = this.getBundleItems(formData);
      const productVariantId = formData.get('id');
      const quantity = this.getQuantity(formData);
      const sectionsToRender = this.cart
        ? this.cart.getSectionsToRender().map((section) => section.id)
        : [];
      
      if (this.cart) {
        this.cart.setActiveElement(document.activeElement);
      }

      if (bundleItems) {
        const body = {
          items: bundleItems,
        };

        if (this.cart) {
          body.sections = sectionsToRender.join(',');
          body.sections_url = window.location.pathname;
        }

        config.body = JSON.stringify(body);
      } else {
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append('sections', sectionsToRender);
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;
      }

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            // CART ERROR EVENT
            document.dispatchEvent(
              new CustomEvent("cart:error", {
                detail: {
                  source: "product-form",
                  productVariantId,
                  errors: response.description,
                  message: response.message,
                },
              })
            );
            // -------------------------------------------
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId, errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (bundleItems && !response.id) {
            response.id = productVariantId;
          }

          // VARIANT ADDED EVENT
          document.dispatchEvent(
            new CustomEvent("variant:add", {
              detail: {
                variant: {
                  id: productVariantId,
                },
                quantity,
                formElement: this.form,
                sectionId: this.dataset.section,
              },
            })
          );
          // -------------------------------------------

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId});

            // CART CHANGE EVENT
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: response,
                bubbles: true,
              })
            );
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
